/**
 * **资产**页（批次 `95` 从「账户」页拆出）—— 只回答"我有多少"
 *
 * ## 🔴 为什么把账户列表从这里搬走
 *
 * 负责人 2026-10-22：「新建一个账户页面，把所有账户按照分类排布，信用卡移到这里，在资产汇总」。
 * 拆开之后两页各答一件事：
 *
 * | 页 | 回答 | 不做什么 |
 * |---|---|---|
 * | **资产**（本页） | 我有多少（净资产 / 资产 / 负债）+ 三个去向入口 | 不列账户明细 |
 * | **账户**（`/accounts`） | 钱分别放在哪、怎么增删改 | 不重复算净资产 |
 *
 * 🔴 数字全部来自 `GET /api/networth`（J 域唯一权威，含 `notReady`/`partial`）——
 * 本页**不自己加总**，也不把"未就绪"当 0（`J域PRD §4.7`）。
 */

import type { ReactElement } from 'react';
import { Alert, Box, Button, Card, CardContent, Divider, Stack, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toCents, type Bucket, type NetWorthNotReady } from '@jiaying/shared';
import { useNavigate } from 'react-router-dom';
import {
  createNetWorthItem,
  deleteNetWorthItem,
  getNetWorth,
  listAccounts,
  listMajorAssets,
  listNetWorthItems,
  updateNetWorthItem,
} from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { invalidateAfterNetWorthItemWrite } from '../features/invalidate.js';
import { toCreateRequest, type NetWorthItemDraft } from '../features/net-worth-item-draft.js';
import { Amount } from '../ui/Amount.js';
import { Loading } from '../ui/Loading.js';
import { NetWorthItemSection } from '../ui/NetWorthItemSection.js';

/**
 * 🆕 一段分桶清单（`DR-057` D3 · 批次 97）—— 资产 10 类 / 负债 6 类共用。
 *
 * 🔴 **零加总**：金额一律读服务端下发的 `bucket.baseCents`（J 域 PRD §4.7），
 * 这里不做任何 `reduce`、不重算比例。
 *
 * 🔴 **「暂无数据」≠ 0**：`notReady` 点名的桶若金额为 0，显示「暂无数据」。
 * 把"没有载体"画成 0，会让用户以为真的没有这类资产
 * —— 与 `SnapshotScope` 把 `0` 和「那类没有历史取值」分开是同一条纪律。
 */
function BucketRows({
  buckets,
  notReady,
  currency,
}: {
  buckets: readonly Bucket[];
  notReady: readonly NetWorthNotReady[];
  currency: string;
}): ReactElement {
  return (
    <Stack spacing={1} sx={{ mt: 1.5 }}>
      {buckets.map((b) => {
        const miss = notReady.find((x) => x.bucketKey === b.key);
        const empty = b.baseCents === 0 && miss !== undefined;
        return (
          <Stack
            key={b.key}
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
            spacing={2}
            data-testid={`bucket-${b.key}`}
          >
            <Typography variant="body2" color={empty ? 'text.disabled' : 'text.primary'}>
              {b.label}
              {b.fxIncomplete ? '（含缺汇率项，合计不完整）' : ''}
            </Typography>
            {empty ? (
              <Typography variant="caption" color="text.disabled">
                暂无数据
              </Typography>
            ) : (
              <Amount cents={b.baseCents} currency={currency} />
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}

export function AssetsPage(): ReactElement {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const networth = useQuery({ queryKey: ['networth'], queryFn: getNetWorth });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const majorAssets = useQuery({ queryKey: ['major-assets'], queryFn: listMajorAssets });
  // 🆕 批 107（K1）：手动资产 / 负债 —— 载体 `net_worth_items` 此前只有读、没有写入口
  const manualItems = useQuery({ queryKey: ['net-worth-items'], queryFn: listNetWorthItems });

  /**
   * 三条写操作共用一个 `busy` / `error`：本区块是**单表单**形态，
   * 同时只可能有一个动作在飞；分开三套状态只会让"哪个错误属于哪次点击"变模糊。
   */
  const save = useMutation({
    mutationFn: (args: { draft: NetWorthItemDraft; editingId: string | null }) =>
      args.editingId === null
        ? createNetWorthItem(toCreateRequest(args.draft))
        : updateNetWorthItem(args.editingId, toCreateRequest(args.draft)),
    onSuccess: () => {
      invalidateAfterNetWorthItemWrite(qc);
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteNetWorthItem(id),
    onSuccess: () => {
      invalidateAfterNetWorthItemWrite(qc);
    },
  });

  if (networth.isPending) return <Loading />;
  if (networth.isError) {
    return <Alert severity="error">净资产读取失败：{String(networth.error)}</Alert>;
  }

  const nw = networth.data;
  const all = accounts.data?.items ?? [];
  const debtSide = all.filter(
    (a) => a.type === 'contra' || a.type === 'credit',
  ).length;
  const majorCount = (majorAssets.data?.items ?? []).filter((i) => i.disposedDate === null).length;
  const nwiList = manualItems.data;
  /** 🔴 服务端错误原文（端侧不编文案）；`ApiError.message` 是可展示的中文原因 */
  const writeError =
    save.error === null
      ? remove.error === null
        ? null
        : remove.error instanceof ApiError
          ? remove.error.message
          : String(remove.error)
      : save.error instanceof ApiError
        ? save.error.message
        : String(save.error);

  return (
    <Stack spacing={2}>
      <Card data-testid="asset-summary" data-anchor="资产汇总">
        <CardContent>
          <Typography variant="caption" color="text.secondary" display="block">
            净资产（{nw.baseCurrency}）
          </Typography>
          <Amount
            cents={toCents(nw.netWorth)}
            currency={nw.baseCurrency}
            colored={false}
            sx={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1.2 }}
            data-testid="asset-networth"
          />
          <Stack direction="row" spacing={4} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                总资产
              </Typography>
              <Amount
                cents={toCents(nw.totalAssets)}
                currency={nw.baseCurrency}
                colored={false}
                sx={{ fontSize: '1rem', fontWeight: 600 }}
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                总负债
              </Typography>
              <Amount
                cents={toCents(nw.totalLiabilities)}
                currency={nw.baseCurrency}
                colored={false}
                sx={{ fontSize: '1rem', fontWeight: 600 }}
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                账户数
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {all.length}
              </Typography>
            </Box>
          </Stack>

          {/* 🔴 未就绪的构成项必须**说出来**（不静默当 0） */}
          {nw.partial && (
            <Alert severity="info" sx={{ mt: 2 }} data-testid="asset-partial">
              还有 {nw.notReady.length} 类构成项暂无取数来源（
              {nw.notReady.map((x) => x.label).join('、')}）—— 上面的合计是<b>下界</b>。
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* 🆕 分类小计（`DR-057` D3 · 批次 97）—— 服务端早就下发 16 个桶，此前一个字都没渲染 */}
      <Card data-testid="asset-buckets" data-anchor="分类小计">
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            资产分类
          </Typography>
          <BucketRows buckets={nw.assets} notReady={nw.notReady} currency={nw.baseCurrency} />
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            负债分类
          </Typography>
          <BucketRows buckets={nw.liabilities} notReady={nw.notReady} currency={nw.baseCurrency} />
          {nw.partial && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              标「暂无数据」的类别目前还没有数据来源 ⇒ 上方合计是**下界**。
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* 🆕 批 107（K1）：手动资产 / 手动负债 —— 「借出去的钱 / 押金 / 收藏品」的唯一入口。
          🔴 这里**不显示折算值、不显示小计**：金额一律原币（`DR-004`），
          合并与折算只发生在服务端 `composeNetWorth()`（J 域唯一权威）。 */}
      {manualItems.isPending ? (
        <Loading py={2} />
      ) : manualItems.isError ? (
        <Alert severity="error" data-testid="nwi-load-error">
          手动资产读取失败：{String(manualItems.error)}
        </Alert>
      ) : (
        <NetWorthItemSection
          items={nwiList?.items ?? []}
          baseCurrency={nwiList?.baseCurrency ?? nw.baseCurrency}
          busy={save.isPending || remove.isPending}
          error={writeError}
          onSubmit={(draft, editingId) => {
            save.mutate({ draft, editingId });
          }}
          onDelete={(id) => {
            remove.mutate(id);
          }}
        />
      )}

      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            去哪看细节
          </Typography>
          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  账户（{all.length}）
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  按分类看每一个账户：现金 / 储蓄 / 信用卡 / 投资 / 内部挂账 / 往来
                </Typography>
              </Box>
              <Button variant="outlined" onClick={() => navigate('/accounts')} data-testid="go-accounts">
                查看账户
              </Button>
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  债权债务（{debtSide}）
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  借出 / 借入 · 应收应付 · 垫付报销 —— 结算在这里；
                  <b>信用卡账单与还款已移到「账户」页</b>
                </Typography>
              </Box>
              <Button variant="outlined" onClick={() => navigate('/debts')} data-testid="go-debts">
                去债权债务
              </Button>
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  重大资产（{majorCount}）
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  房产 / 车辆 / 收藏 · 购入 → 持有 → 处置全生命周期
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={() => navigate('/major-assets')}
                data-testid="go-major-assets"
              >
                查看重大资产
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
